/* Gallery Focal: comandi diretti e zoom locale, senza un secondo visualizzatore. */
(() => {
  if (customElements.get('extro-product-gallery')) return;
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.carousel = this.querySelector('flickity-carousel');
      this.controls = this.querySelector('.ep-gallery-controls');
      this.dots = this.querySelector('.ep-gallery-dots');
      this.resetButton = this.querySelector('.ep-gallery-reset');
      this.status = this.querySelector('.ep-gallery-status');
      this.scale = 1; this.x = 0; this.y = 0;
      const refresh = () => this.refresh();
      this.carousel.addEventListener('flickity:ready', refresh, { signal });
      this.carousel.addEventListener('flickity:slide-changed', refresh, { signal });
      this.addEventListener('click', event => {
        const step = event.target.closest('[data-gallery-step]');
        const dot = event.target.closest('[data-gallery-index]');
        if (step) this.move(Number(step.dataset.galleryStep));
        else if (dot) this.select(Number(dot.dataset.galleryIndex));
        else if (event.target.closest('.ep-gallery-reset')) this.resetZoom();
      }, { signal });
      // Sulle foto touch gestiamo swipe e pinch; i video conservano i controlli nativi.
      this.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' && this.photoTarget(event.target)) event.stopPropagation();
      }, { capture: true, signal });
      this.addEventListener('touchstart', event => this.touchStart(event), { capture: true, passive: false, signal });
      this.addEventListener('touchmove', event => this.touchMove(event), { capture: true, passive: false, signal });
      this.addEventListener('touchend', event => this.touchEnd(event), { capture: true, passive: false, signal });
      this.addEventListener('touchcancel', () => { this.gesture = null; this.resetZoom(); }, { signal });
      this.addEventListener('gesturestart', event => {
        if (this.hasAttribute('pinch-zoom') && this.photoTarget(event.target)) event.preventDefault();
      }, { passive: false, signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.scale > 1) { this.resetZoom(); this.controls.querySelector('button')?.focus(); }
      }, { signal });
      this.resize = new ResizeObserver(entries => {
        const height = `${entries[0].contentRect.height}px`;
        const media = this.closest('product-media');
        if (media && media.style.getPropertyValue('--ep-gallery-height') !== height) media.style.setProperty('--ep-gallery-height', height);
        if (this.scale > 1) this.resetZoom();
      });
      this.resize.observe(this);
      customElements.whenDefined('flickity-carousel').then(() => { if (this.isConnected) this.refresh(); });
    }
    disconnectedCallback() {
      this.abort?.abort(); this.resize?.disconnect(); cancelAnimationFrame(this.frame);
      this.resetZoom();
    }
    async refresh() {
      const instance = await this.carousel.flickityInstance;
      if (!this.isConnected) return;
      this.instance = instance;
      this.items = instance ? instance.cells.map(cell => cell.element) : Array.from(this.carousel.querySelectorAll('.product__media-item:not(.is-filtered)'));
      const selected = instance?.selectedElement || this.items[0];
      if (selected !== this.selected) { this.resetZoom(); this.selected = selected; }
      this.index = Math.max(0, this.items.indexOf(selected));
      this.dataset.mediaType = selected?.dataset.mediaType || '';
      this.controls.hidden = this.items.length < 2;
      const key = this.items.map(item => item.id).join('|');
      if (key !== this.itemsKey) {
        this.itemsKey = key;
        this.dots.style.setProperty('--ep-gallery-count', this.items.length);
        this.dots.replaceChildren(...this.items.map((item, index) => {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'ep-gallery-dot'; button.dataset.galleryIndex = index;
          const type = item.dataset.mediaType === 'image' ? 'Foto' : ['video', 'external_video'].includes(item.dataset.mediaType) ? 'Video' : 'Modello';
          button.setAttribute('aria-label', `${type} ${index + 1} di ${this.items.length}`);
          button.setAttribute('aria-controls', item.id);
          return button;
        }));
      }
      Array.from(this.dots.children).forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.index)));
      this.status.textContent = this.items.length ? `${this.index + 1} di ${this.items.length}` : '';
    }
    select(index) {
      if (!this.instance || this.items.length < 2) return;
      this.resetZoom();
      const item = this.items[(index + this.items.length) % this.items.length];
      this.carousel.select(`#${CSS.escape(item.id)}`);
    }
    move(step) { this.select(this.index + step); }
    photoTarget(target) { return target.closest?.('.product__media-item[data-media-type="image"] .product__media-image-wrapper'); }
    resetZoom() {
      cancelAnimationFrame(this.frame);
      if (this.zoomImage) { this.zoomImage.style.removeProperty('transform'); this.zoomImage.style.removeProperty('will-change'); }
      this.zoomImage = null; this.scale = 1; this.x = 0; this.y = 0; this.gesture = null;
      this.classList.remove('is-zoomed');
      if (this.resetButton) this.resetButton.hidden = true;
    }
    touchStart(event) {
      const wrapper = this.photoTarget(event.target);
      if (!wrapper) return;
      event.stopPropagation();
      const first = event.touches[0];
      if (event.touches.length === 2 && this.hasAttribute('pinch-zoom')) {
        event.preventDefault();
        const second = event.touches[1], rect = wrapper.getBoundingClientRect();
        this.zoomImage = wrapper.querySelector('img');
        this.zoomImage.style.willChange = 'transform';
        const cx = (first.clientX + second.clientX) / 2 - rect.left - rect.width / 2;
        const cy = (first.clientY + second.clientY) / 2 - rect.top - rect.height / 2;
        this.gesture = { type: 'pinch', distance: Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY), scale: this.scale, cx, cy, x: this.x, y: this.y, rect };
      } else if (event.touches.length === 1) {
        this.gesture = { type: this.scale > 1 ? 'pan' : 'swipe', startX: first.clientX, startY: first.clientY, x: this.x, y: this.y, rect: wrapper.getBoundingClientRect() };
      } else { this.gesture = null; }
    }
    touchMove(event) {
      const gesture = this.gesture;
      if (!gesture || gesture.type === 'swipe') return;
      event.preventDefault(); event.stopPropagation();
      if (gesture.type === 'pinch' && event.touches.length === 2) {
        const [a, b] = event.touches;
        this.scale = Math.min(3, Math.max(1, gesture.scale * Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) / Math.max(1, gesture.distance)));
        const ratio = this.scale / gesture.scale;
        const cx = (a.clientX + b.clientX) / 2 - gesture.rect.left - gesture.rect.width / 2;
        const cy = (a.clientY + b.clientY) / 2 - gesture.rect.top - gesture.rect.height / 2;
        this.x = cx - (gesture.cx - gesture.x) * ratio;
        this.y = cy - (gesture.cy - gesture.y) * ratio;
      } else if (gesture.type === 'pan' && event.touches.length === 1) {
        this.x = gesture.x + event.touches[0].clientX - gesture.startX;
        this.y = gesture.y + event.touches[0].clientY - gesture.startY;
      }
      this.x = Math.max(-gesture.rect.width * (this.scale - 1) / 2, Math.min(gesture.rect.width * (this.scale - 1) / 2, this.x));
      this.y = Math.max(-gesture.rect.height * (this.scale - 1) / 2, Math.min(gesture.rect.height * (this.scale - 1) / 2, this.y));
      this.classList.toggle('is-zoomed', this.scale > 1.01);
      this.resetButton.hidden = this.scale <= 1.01;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (this.zoomImage) this.zoomImage.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
      });
    }
    touchEnd(event) {
      const gesture = this.gesture;
      if (!gesture) return;
      this.gesture = null;
      if (gesture.type !== 'swipe') {
        event.preventDefault(); event.stopPropagation();
        if (event.touches.length === 1) this.touchStart(event);
        else if (this.scale <= 1.01) this.resetZoom();
        return;
      }
      if (event.touches.length || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - gesture.startX, dy = event.changedTouches[0].clientY - gesture.startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        event.preventDefault(); event.stopPropagation(); this.move(dx < 0 ? 1 : -1);
      }
    }
  }
  customElements.define('extro-product-gallery', ProductGallery);
})();
